import { Component, ElementRef, OnInit, ViewChild, signal } from '@angular/core';
import { startWith } from 'rxjs/operators';

import { renderMarkdown } from '../../../global/markdown/render-markdown';
import { BaseFormControl } from '../base-control';
import { FormMarkdownField } from '../form-field';

import { applyMarkdown, MarkdownAction } from './apply-markdown';

interface ToolbarButton {
    action: MarkdownAction;
    label: string;
    /** The letter this action answers to with ctrl/cmd held, if it has one. */
    shortcut?: string;
    /** An ionicon name, for the actions that have a recognisable one. */
    icon?: string;
    /** A letter instead, for the ones where the letter IS the convention - B, I, 1. */
    glyph?: string;
}

/**
 * The toolbar, defined here rather than as a class field so the component references one list
 * instead of rebuilding it per instance - and so a test can reach it without constructing the
 * component, which is how these specs are written.
 *
 * B and I are drawn as letters, not icons. Ionicons has no bold or italic glyph, and the nearest
 * pair - text-outline and text - differ only in being outlined or filled, so the filled one read
 * as the bolder of the two and they looked swapped. A weighted B and a slanted I are what every
 * editor uses and need no interpreting.
 */
export const TOOLBAR_BUTTONS: ToolbarButton[] = [
    { action: 'bold', glyph: 'B', label: 'Bold', shortcut: 'b' },
    { action: 'italic', glyph: 'I', label: 'Italic', shortcut: 'i' },
    { action: 'bulletList', icon: 'list-outline', label: 'Bulleted list' },
    { action: 'numberList', glyph: '1.', label: 'Numbered list' },
    { action: 'quote', icon: 'chatbox-outline', label: 'Quote' },
    { action: 'link', icon: 'link-outline', label: 'Link' }
];

/**
 * Mac keyboards say Cmd, everything else says Ctrl, and the shortcut accepts either - so this is
 * about naming the key the organiser is actually looking at, not about behaviour.
 */
const MODIFIER_LABEL = /Mac|iPhone|iPad/i.test(navigator?.platform || navigator?.userAgent || '') ? '\u2318' : 'Ctrl+';

/**
 * A textarea that stores markdown, with buttons that write the markers and a live preview beside
 * it.
 *
 * The buttons are the point. Most organisers have never heard of markdown, and asking them to
 * remember that two asterisks mean bold is asking them not to use it. Pressing B with a word
 * selected is a thing anyone can do, and what gets stored is still plain markdown text - no HTML,
 * nothing to sanitize on the way in, and it diffs and prints like the prose it is.
 *
 * The preview is the other half of that: markdown looks like punctuation until you see what it
 * does, so the rendered result sits alongside the box rather than being discovered after saving.
 */
@Component({
    standalone: false,
    selector: 'abs-markdown-field',
    template: `
        <div *ngIf="parentFormGroup" [formGroup]="parentFormGroup">
            <div class="form-group markdown-field">
                <label *ngIf="!field.hideLabel"
                    >{{ field.label || (field.name | formLabel)
                    }}<span class="required-marker" *ngIf="field.required" aria-hidden="true">*</span>
                    <ion-icon class="info" *ngIf="field.description" name="information-circle" (click)="showDescription($event)"></ion-icon
                ></label>

                <div class="markdown-field-toolbar">
                    <!-- mousedown is where the work starts, not click. The default action of
                         pressing a button is to move focus, which takes the textarea's selection
                         with it - so by the time click fires there is nothing selected to format.
                         Preventing it keeps the caret exactly where the organiser left it. -->
                    <button
                        type="button"
                        class="markdown-field-button markdown-field-button--{{ button.action }}"
                        *ngFor="let button of buttons"
                        [attr.aria-label]="button.label"
                        [title]="titleFor(button)"
                        (mousedown)="rememberSelection($event)"
                        (click)="apply(button.action)"
                    >
                        @if (button.glyph) {
                            <span class="markdown-field-glyph">{{ button.glyph }}</span>
                        } @else {
                            <ion-icon [name]="button.icon"></ion-icon>
                        }
                    </button>
                </div>

                <div class="markdown-field-panes">
                    <ion-textarea
                        #input
                        auto-grow
                        rows="6"
                        class="markdown-field-input"
                        [formControlName]="field.name"
                        [disabled]="field.readOnly"
                        (keydown)="onKeyDown($event)"
                    ></ion-textarea>

                    <div class="markdown-field-preview">
                        <span class="markdown-field-preview-label">Preview</span>
                        @if (preview()) {
                            <div class="markdown-body" [innerHTML]="preview()"></div>
                        } @else {
                            <p class="markdown-field-preview-empty">Nothing to preview yet.</p>
                        }
                    </div>
                </div>

                <button type="button" class="markdown-field-help-toggle" (click)="showHelp.set(!showHelp())">
                    {{ showHelp() ? 'Hide formatting help' : 'Formatting help' }}
                </button>
                @if (showHelp()) {
                    <table class="markdown-field-help">
                        @for (row of help; track row.syntax) {
                            <tr>
                                <td>
                                    <code>{{ row.syntax }}</code>
                                </td>
                                <td>{{ row.means }}</td>
                            </tr>
                        }
                    </table>
                }
            </div>
        </div>
    `
})
export class MarkdownFieldComponent extends BaseFormControl implements OnInit {
    declare field: FormMarkdownField;

    /**
     * `{ read: ElementRef }` is load-bearing. IonicModule declares ion-textarea as an Angular
     * component, so a bare @ViewChild returns the IonTextarea INSTANCE, whose `nativeElement` is
     * undefined - the lookup below then found no textarea, no selection was ever captured, and
     * every button appended its markers to the end of the box instead of wrapping the selection.
     */
    @ViewChild('input', { read: ElementRef }) input?: ElementRef<HTMLElement>;

    readonly showHelp = signal(false);
    readonly preview = signal('');

    /** Where the caret was when a button was pressed, captured before focus can move. */
    private selection: { start: number; end: number } | null = null;

    /**
     * Values to go back to, most recent last.
     *
     * The component keeps its own history because a toolbar press changes the text through
     * setValue, and the browser's undo stack cannot see a programmatic write - so native Ctrl+Z
     * would skip straight past the formatting to whatever was typed before it, or do nothing.
     * Only toolbar edits are recorded; typing is left to the browser, which already handles it.
     */
    private history: { value: string; start: number; end: number }[] = [];

    /** Bounded because an organiser can press buttons all afternoon and this is a form field. */
    private static readonly MAX_HISTORY = 50;

    // English, like every other admin form control and like the event form's own labels. These
    // are authoring tools, not player-facing copy.
    readonly buttons = TOOLBAR_BUTTONS;

    readonly help = [
        { syntax: '**bold**', means: 'Bold' },
        { syntax: '*italic*', means: 'Italic' },
        { syntax: '- item', means: 'Bulleted list' },
        { syntax: '1. item', means: 'Numbered list' },
        { syntax: '## Heading', means: 'Heading' },
        { syntax: '[text](https://...)', means: 'Link' },
        { syntax: '(blank line)', means: 'New paragraph' }
    ];

    override ngOnInit() {
        super.ngOnInit();

        const control = this.control;
        control?.valueChanges.pipe(startWith(control.value)).subscribe((value) => this.preview.set(renderMarkdown(value)));
    }

    /**
     * Captures the selection and stops the button stealing focus.
     *
     * Both halves matter: preventDefault keeps the textarea focused, so the caret stays visible and
     * a second press can undo the first; the captured range means the edit is still right even if
     * something else moves focus first.
     */
    rememberSelection(event: Event) {
        event.preventDefault();
        this.captureSelection();
    }

    /**
     * Reads the live selection out of the textarea.
     *
     * apply() prefers this captured range over whatever the textarea reports at the time, so it
     * has to be refreshed before every action - including the keyboard ones. Left stale, a
     * shortcut pressed after a toolbar press would format wherever that press finished rather
     * than where the caret is now.
     */
    private captureSelection() {
        const textarea = this.nativeTextarea();
        this.selection = textarea ? { start: textarea.selectionStart ?? 0, end: textarea.selectionEnd ?? 0 } : null;
    }

    /** "Bold (⌘B)" - the modifier named the way the keyboard in front of the organiser names it. */
    titleFor(button: ToolbarButton): string {
        return button.shortcut ? `${button.label} (${MODIFIER_LABEL}${button.shortcut.toUpperCase()})` : button.label;
    }

    async apply(action: MarkdownAction) {
        const control = this.control;
        if (!control) {
            return;
        }

        const textarea = this.nativeTextarea();
        const value: string = control.value ?? '';
        // Falling back to the end of the text, not the start: appending to what is there is a far
        // less destructive guess than inserting markers in front of everything.
        const start = this.selection?.start ?? textarea?.selectionStart ?? value.length;
        const end = this.selection?.end ?? textarea?.selectionEnd ?? start;

        const edit = applyMarkdown(value, start, end, action);

        this.history.push({ value, start, end });
        if (this.history.length > MarkdownFieldComponent.MAX_HISTORY) {
            this.history.shift();
        }

        control.setValue(edit.value);
        control.markAsDirty();
        this.selection = { start: edit.selectionStart, end: edit.selectionEnd };

        if (!textarea) {
            return;
        }
        // ion-textarea writes the new value into its inner textarea on its own schedule, and that
        // write puts the caret at the end. Restoring afterwards is what leaves the formatted text
        // selected, so pressing the same button again undoes it.
        await new Promise((resolve) => setTimeout(resolve));
        textarea.focus();
        textarea.setSelectionRange(edit.selectionStart, edit.selectionEnd);
    }

    /**
     * Ctrl+Z / Cmd+Z undoes a toolbar press.
     *
     * Only handled when this component has something of its own to undo. With an empty history the
     * event is left alone, so the browser's own undo keeps working on the text the organiser typed
     * - intercepting it unconditionally would break the thing it is meant to improve.
     */
    onKeyDown(event: KeyboardEvent) {
        if (!event.ctrlKey && !event.metaKey) {
            return;
        }

        const key = event.key?.toLowerCase();

        // Undo first: it is the one that has to decline when there is nothing of ours to take
        // back, so that the browser's own undo of typing still works.
        if (key === 'z' && !event.shiftKey) {
            if (!this.history.length) {
                return;
            }
            event.preventDefault();
            void this.undo();
            return;
        }

        const button = this.buttons.find((b) => b.shortcut === key);
        if (!button || event.shiftKey || event.altKey) {
            return;
        }

        // Taken from the browser deliberately - ctrl/cmd+B is bookmarks and cmd+I is page info,
        // neither of which anyone wants from inside a text box they are writing in.
        event.preventDefault();
        this.captureSelection();
        void this.apply(button.action);
    }

    async undo() {
        const previous = this.history.pop();
        const control = this.control;
        if (!previous || !control) {
            return;
        }

        control.setValue(previous.value);
        control.markAsDirty();
        this.selection = { start: previous.start, end: previous.end };

        const textarea = this.nativeTextarea();
        if (!textarea) {
            return;
        }
        await new Promise((resolve) => setTimeout(resolve));
        textarea.focus();
        textarea.setSelectionRange(previous.start, previous.end);
    }

    private get control() {
        return this.parentFormGroup?.controls[this.field.name];
    }

    /**
     * The real <textarea> inside Ionic's web component, read synchronously.
     *
     * `getInputElement()` is Ionic's async accessor, but this also runs inside the mousedown
     * handler, and awaiting anything there means the default action has already happened and the
     * selection has gone.
     */
    private nativeTextarea(): HTMLTextAreaElement | null {
        const ref = this.input as any;
        // `el` covers an Ionic component instance, in case this is ever bound without the
        // ElementRef read above. Both shapes rather than one, because getting this wrong fails
        // silently - the buttons still appear to work, they just format the wrong place.
        const host: HTMLElement | undefined = ref?.nativeElement ?? ref?.el;
        if (!host) {
            return null;
        }
        if (host.tagName === 'TEXTAREA') {
            return host as HTMLTextAreaElement;
        }
        return host.querySelector?.('textarea') ?? (host as any).shadowRoot?.querySelector?.('textarea') ?? null;
    }
}
